import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/uploadFile';
import { deleteFile } from '@/integrations/supabase/storage';

export interface Haul {
  id: string;
  name: string;
  thumbnail_image: string;
  youtube_link?: string | null;
  item_ids: number[];
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface HaulFormData {
  name: string;
  thumbnail_image: File | string | null;
  youtube_link?: string | null;
  item_ids: number[];
}

export type ProductItem = ClothingItem | ShoeItem;

export const getHauls = async (): Promise<Haul[]> => {
  const { data, error } = await supabase
    .from('hauls')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as Haul[];
};

export const getProducts = async (): Promise<ProductItem[]> => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    // .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as ProductItem[];
};

/**
 * Search products server-side by name or exact _id match, with pagination.
 * - If the query is all digits (e.g. "4694"), it matches exact _id OR name contains it.
 * - Otherwise it matches name.ilike %query%.
 * Returns up to `perPage` results, ordered by _id descending.
 */
export const searchProducts = async (
  query: string,
  page: number = 1,
  perPage: number = 30
): Promise<{ data: ProductItem[]; count: number }> => {
  const trimmed = query.trim();

  // No query → return most recent products
  if (!trimmed) {
    const start = (page - 1) * perPage;
    const { data, error, count } = await supabase
      .from('products')
      .select('*', { count: 'exact' })
      .order('_id', { ascending: false })
      .range(start, start + perPage - 1);

    if (error) throw error;
    return { data: (data || []) as ProductItem[], count: count || 0 };
  }

  // Numeric query → exact _id match + name fallback
  const numericId = Number(trimmed);
  if (!isNaN(numericId)) {
    // Search by exact _id OR name contains the number
    const { data, error, count } = await supabase
      .from('products')
      .select('*', { count: 'exact' })
      .or(`_id.eq.${numericId},name.ilike.%${trimmed}%`)
      .order('_id', { ascending: false })
      .range(0, perPage - 1);

    if (error) throw error;
    return { data: (data || []) as ProductItem[], count: count || 0 };
  }

  // Text query → name search
  const start = (page - 1) * perPage;
  const { data, error, count } = await supabase
    .from('products')
    .select('*', { count: 'exact' })
    .ilike('name', `%${trimmed}%`)
    .order('_id', { ascending: false })
    .range(start, start + perPage - 1);

  if (error) throw error;
  return { data: (data || []) as ProductItem[], count: count || 0 };
};

export const getHaul = async (id: string): Promise<Haul> => {
  const { data, error } = await supabase
    .from('hauls')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Haul;
};

export const createHaul = async (formData: HaulFormData): Promise<Haul> => {
  let thumbnailUrl = '';

  if (formData.thumbnail_image instanceof File) {
    thumbnailUrl = await uploadFile(formData.thumbnail_image);
  } else if (typeof formData.thumbnail_image === 'string') {
    thumbnailUrl = formData.thumbnail_image;
  } else {
    throw new Error('Thumbnail image is required');
  }

  const { data: { user } } = await supabase.auth.getUser();
  const timestamp = new Date().toISOString();

  // Ensure item_ids is an array
  const itemIds = Array.isArray(formData.item_ids) ? formData.item_ids : [];

  const insertData = {
    name: formData.name,
    thumbnail_image: thumbnailUrl,
    youtube_link: formData.youtube_link ?? null,
    item_ids: itemIds,
    created_by: user?.id ?? null,
    created_at: timestamp,
    updated_at: timestamp,
  };

  console.log('Creating haul with data:', { ...insertData, thumbnail_image: '[URL]' });

  const { data, error } = await supabase
    .from('hauls')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Supabase error creating haul:', error);
    console.error('Error details:', JSON.stringify(error, null, 2));
    throw new Error(error.message || 'Failed to create haul');
  }
  return data as Haul;
};

export const updateHaul = async (id: string, formData: HaulFormData): Promise<Haul> => {
  let thumbnailUrl: string | undefined;

  if (formData.thumbnail_image instanceof File) {
    thumbnailUrl = await uploadFile(formData.thumbnail_image);
  } else if (typeof formData.thumbnail_image === 'string') {
    thumbnailUrl = formData.thumbnail_image;
  }

  const timestamp = new Date().toISOString();
  // Ensure item_ids is an array
  const itemIds = Array.isArray(formData.item_ids) ? formData.item_ids : [];
  
  const updatePayload: Record<string, unknown> = {
    name: formData.name,
    youtube_link: formData.youtube_link ?? null,
    item_ids: itemIds,
    updated_at: timestamp,
  };
  if (thumbnailUrl !== undefined) {
    updatePayload.thumbnail_image = thumbnailUrl;
  }

  console.log('Updating haul with data:', { ...updatePayload, thumbnail_image: thumbnailUrl ? '[URL]' : undefined });

  const { data, error } = await supabase
    .from('hauls')
    .update(updatePayload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Supabase error updating haul:', error);
    console.error('Error details:', JSON.stringify(error, null, 2));
    throw new Error(error.message || 'Failed to update haul');
  }
  return data as Haul;
};

export const deleteHaul = async (id: string): Promise<void> => {
  // First fetch the haul to get the thumbnail URL
  const { data: haul, error: fetchError } = await supabase
    .from('hauls')
    .select('thumbnail_image')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Delete the thumbnail from storage if it exists
  if (haul?.thumbnail_image) {
    try {
      const url = new URL(haul.thumbnail_image);
      // Extract the filename from the public URL:
      // https://<project>.supabase.co/storage/v1/object/public/img/<filename>
      // `deleteFile` already targets the 'img' bucket, so we only need the filename
      const pathParts = url.pathname.split('/object/public/');
      if (pathParts.length > 1) {
        const bucketAndFile = pathParts[1];            // e.g. "img/filename.jpg"
        const fileName = bucketAndFile.split('/').slice(1).join('/'); // just "filename.jpg"
        await deleteFile(fileName);
      }
    } catch (err) {
      console.error('Error deleting thumbnail image from storage:', err);
      // Don't block the haul deletion if image deletion fails
    }
  }

  // Then delete the haul record
  const { error } = await supabase.from('hauls').delete().eq('id', id);
  if (error) throw error;
};
